import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Navbar from '../../../../../components/Atoms/Navbar/Navbar.js';
import BookSessionForm from '../../../../../components/Molecules/BookSessionForm/BookSessionForm';
import MusicianCard from '../../../../../components/Molecules/MusicianCard/MusicianCard.js';
import Billboard from '../../../../../components/Molecules/Billboard/Billboard';
import SidePane from '../../../../../components/Molecules/SidePane/SidePane.js';
import { config } from '../../../../../config/musicianConfigs';
import styles from '../Explore.module.scss';
import useIsPartnerGuard from '../../../../../guards/useIsPartnerDashboard.guard';
import { getCurrentlyBookingArtistConfig, getExploreFilters, getNextAvailableSessions } from '../../../../../store/actions/musicians.actions';
import Spinner from '../../../../../components/Atoms/Spinner/Spinner';
import { UserStatuses } from '../../../../../enums/user-statuses.enum';
import { getTracks } from '../../../../../store/actions/sessions.actions';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { setIsShowGlobalLoader } from '../../../../../store/slices/global.slice';
import { setIsUpsellPlansModal } from '../../../../../store/slices/modals.slice';
import MusiciansService from '../../../../../services/musicians/musicians.service';
import StrapiService from '../../../../../services/cms/cms.service';
import { selectTracks, setSession } from '../../../../../store/slices/sessions.slice';
import moment from 'moment-timezone';
import ProFilters from './pro-filters/pro-filters';
import { selectExploreFilters, selectNextAvailableSessions } from '../../../../../store/slices/musicians.slice';
import Icon from '../../../../../components/Atoms/Icon/Icon';
import classNames from 'classnames';

const AVAILABILITY_DAYS = 60;
const DEFAULT_CONFIG = {
  bookingOptions: null,
  fileUploadRules: null,
  deliverablesOptions: {
    default: 'Stems to be imported in a different DAW',
    options: ['Stems to be imported in a different DAW']
  }
};

const PARTNERS_ORDER = [
  'clare-dove',
  'devin-malloy',
  'pedro-araujo',
  'chris-barber',
  'bruno-migliari',
  'marta-garrett',
  'gabor-udvarhelyi',
  'ben-trigg',
  'damian-bolotin',
  'yuri-villar',
  'chris-ott',
  'bence-taborszky',
  'felipe-ribeiro',
  'luiz-tornaghi',
  'arthur-romio',
  'joao-paulo-drumond',
  'davi-mello',
  'fernando-moreno',
  'pablo-arruda',
  'antonio-guerra',
  'vitaliy-tkachuk',
  'samuel-da-silva',
  'ayran-nicodemo',
  'henrique-vilhena',
  'nuno-fernandes'
];

const ProExplore = () => {
  const dispatch = useDispatch();
  const { user, user: { timezone } } = useSelector(state => state.userSlice, shallowEqual);
  const tracks = useSelector(selectTracks, shallowEqual);
  const exploreFilters = useSelector(selectExploreFilters, shallowEqual);
  const history = useHistory();
  const { slug } = useParams();
  const location = useLocation();
  const nextAvailableSessions = useSelector(selectNextAvailableSessions, shallowEqual);

  const [showPanel, setShowPanel] = useState(false);
  const [slotsToShow, setSlotsToShow] = useState([]);
  const [selectedMusician, setSelectedMusician] = useState(null);
  const [selectedMusicianConfig, setSelectedMusicianConfig] = useState(null);
  const [partnersToDisplay, setPartnersToDisplay] = useState([]);
  const [partners, setPartners] = useState([]);
  const [serviceFilters, setServiceFilters] = useState([]);
  const [timeFilters, setTimeFilters] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  useIsPartnerGuard('/sessions');

  const getPartners = async filters => {
    try {
      setIsLoading(true);
      const { partners: partnersList } = (await MusiciansService.gerPartners(filters)).data;
      setPartners(partnersList);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!timeFilters) {
      return;
    }
    getPartners(timeFilters).finally();
  }, [timeFilters, timezone]);

  useEffect(() => {
    if (serviceFilters.length === 0) {
      setPartnersToDisplay(partners);
      return;
    }
    const filteredPartners = partners.map(partner =>
      partner.services.some(service => serviceFilters.includes(service)) ? partner : null).filter(p => !!p);
    setPartnersToDisplay(filteredPartners);
  }, [partners, serviceFilters]);

  const orderPartners = useCallback((partners, definedOrder) => {
    const orderedPartners = [];
    definedOrder.forEach(slug => {
      const partner = partners.find(partner => partner.slug === slug);
      if (partner) {
        orderedPartners.push(partner);
      }
    });
    // Now add the rest of the partners
    partners.forEach(partner => {
      if (!orderedPartners.includes(partner)) {
        orderedPartners.push(partner);
      }
    });
    return orderedPartners;
  }, []);

  useEffect(() => {
    const getData = async () => {
      const promises = [];
      if (!exploreFilters?.length) {
        promises.push(dispatch(getExploreFilters()));
      }
      if (!nextAvailableSessions?.length) {
        promises.push(dispatch(getNextAvailableSessions()));
      }
      if (!tracks?.length) {
        promises.push(dispatch(getTracks()));
      }
      await Promise.all(promises);
    };
    getData().finally();
  }, [dispatch, exploreFilters?.length, nextAvailableSessions?.length, tracks]);

  const getMusicianAvailability = useCallback(async ({ id }) => {
    const slots = (await MusiciansService.getMusicianAvailability(id, AVAILABILITY_DAYS)).data;
    setSlotsToShow(slots);
  }, []);

  const getMusicianConfiguration = useCallback(async ({ id }) => {
    try {
      const res = await MusiciansService.getMusicianConfiguration(id);
      setSelectedMusicianConfig(res.data);
    } catch (err) {
      setSelectedMusicianConfig(DEFAULT_CONFIG);
      throw new Error(err);
    }
  }, []);

  const selectMusician = useCallback(async musician => {
    dispatch(setIsShowGlobalLoader(true));
    try {
      await Promise.all([
        getMusicianAvailability(musician),
        getMusicianConfiguration(musician),
        dispatch(getCurrentlyBookingArtistConfig(musician.slug))
      ]);
      setSelectedMusician(musician);
      setShowPanel(true);
    } catch (e) {
      console.error(e);
    } finally {
      dispatch(setIsShowGlobalLoader(false));
    }
  }, [getMusicianAvailability, getMusicianConfiguration, dispatch]);

  useEffect(() => {
    if (!slug) {
      return;
    }
    if (location.search) {
      history.push(location.pathname);
    }
    const musicianToOpen = partners?.find(musician => musician.slug === slug);
    if (!musicianToOpen) {
      history.push(location.pathname);
      // history.push('/explore');
      return;
    }
    selectMusician(musicianToOpen).finally();
  }, [partners, selectMusician, slug, dispatch, history, location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname == '/offers') {
      dispatch(setIsUpsellPlansModal(true));
      history.push('/explore');
      return;
    }
  }, [dispatch, history, location.pathname, location.search]);


  const [width, setWidth] = useState(0);

  useEffect(() => {
    setWidth(window.innerWidth);
    const updateWidth = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const onPanelClose = () => {
    dispatch(setSession(null));
    history.push('/explore');
    setShowPanel(false);
  };

  const { startDate: filterStartDate, endDate: filterEndDate, startTime, endTime } = timeFilters || {};
  const filtersApplied = serviceFilters.length || filterStartDate || filterEndDate || startTime || endTime;

  return (
    <>
      <div className={styles.wrapper}>
        <Navbar currentPath='musicians' spaceBottom={false} isTrial={user?.status === UserStatuses.TRIAL} />

        {exploreFilters?.length ? <div className={styles.proExploreContainer}>
            <ProFilters
              filters={exploreFilters}
              serviceFilters={serviceFilters}
              timeFilters={timeFilters}
              setServiceFilters={setServiceFilters}
              setTimeFilters={setTimeFilters}
              setIsMobileFiltersOpen={setIsMobileFiltersOpen}
            />
            <div className={styles.exploreBody}>
              <div className={classNames(styles.billboard)}>
                <Billboard />
              </div>

                {!isMobileFiltersOpen && <div className={classNames(styles.musiciansGrid, filtersApplied && styles.filtersApplied)}>
                  {isLoading ? (
                    <Spinner className={styles.spinner} isBlue />
                  ) : partnersToDisplay.length ? (
                    orderPartners(partnersToDisplay, nextAvailableSessions.map(x => x.slug)).map(musician => {
                      /* Musicians that don't have an image throws `undefined` which makes the whole code breaks, did this quick fix until it's resolved */
                      //const tempImg = musician.image_url ? musician.image_url : (musician.image?.url ? musician.image?.url : "/images/musicians/avatar.png");

                      const tempImg = musician.image ?? `../../../assets/images/musicians-avatars/${musician.name}.png`;
                      return (
                        <MusicianCard
                          key={musician.id}
                          id={musician.id}
                          avatar={tempImg}
                          name={musician.name}
                          bio={musician.cardDescription}
                          instrument={musician.title}
                          musician={musician}
                          onClick={() => selectMusician(musician)}
                          externalUrl={config.linksByName[musician.name]}
                          musicianUrl={`${process.env.REACT_APP_MUSICIAN_PROFILES_DOMAIN}/roster/${musician.slug}`}
                        />
                      );
                    })
                  ) : <div className={styles.noAvailableSessionContainer}>
                    <Icon name={'magGlass'} />
                    <p>{'There are no available sessions for the chosen filters. Please try another service or date range!'}</p>
                  </div>}
                </div>}
              </div>
            </div> :
            <Spinner className={styles.spinner} isBlue />
          }
        </div>

      <SidePane
        isOpen={showPanel}
        isLarge
        onRequestClose={onPanelClose}
      >
        {selectedMusician && (
          <>
            <BookSessionForm
              musician={selectedMusician}
              musicianConfiguration={selectedMusicianConfig}
              instrument={selectedMusician.title}
              timezone={user?.timezone || moment.tz.guess()}
              slots={slotsToShow}
              tracks={tracks}
              mobileVersion={width < 576}
              onClose={() => {
                setShowPanel(false);
              }}
            />
          </>
        )}
      </SidePane>
    </>
  );
};

export default ProExplore;
